import { observable } from "mobx"
import { toast } from "sonner"

import { BranchSelection } from "@/components/BranchSelection/BranchSelection.ts"
import { createCompletionProvider } from "@/components/PromptInput/Completion/completionProviders.ts"
import { PromptInputModelImpl } from "@/components/PromptInput/PromptInputModel.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { successfulAttachments } from "@/lib/std.ts"
import { TaskSettingsModel } from "@/routes/tasks/TaskNewScene/TaskSettingsModel.ts"
import { tasksByRepoKey } from "@/routes/tasks/tasksHooks.ts"

import type { PromptInputModel, PromptInputSubmitArgs } from "@/components/PromptInput/PromptInputModel.ts"
import type { RouterType } from "@/context.ts"
import type { AiAgentTask, AiAgentTaskSettings, PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import type { TaskTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

const log = getLogger("NewTaskPromptInputModel")

export class NewTaskPromptInputModel extends Model {
  readonly taskSettings = new TaskSettingsModel(false)
  readonly inputModel: PromptInputModel

  constructor(
    router: RouterType,
    private readonly context: OrgContext,
    private readonly project: PR_Project2,
    private readonly onSubmit: () => Promise<void>,
    private readonly onTaskCreated: (task: AiAgentTask, view?: TaskViewId, tab?: TaskTabId) => Promise<void>
  ) {
    super("NewTaskPromptInputModel")

    this.inputModel = new PromptInputModelImpl({
      name: "NewTaskPromptInputModel",
      router: router,
      submit: this.submit,
      autoFocus: observable.box(true),
      resetOnNavigation: true,
      persistence: this.context.persistence.subStorage(this.project.key.key),
      completionProvider: createCompletionProvider(context.queries, {
        projectKey: this.project.key,
        repo: this.repositoryName,
        commit: null
      }, context),
      orgContext: this.context
    })

    this.inputModel.branchSelection.set(
      new BranchSelection(this.context, this.lifetime, this.project.key, this.repositoryName)
    )
  }

  private get repositoryName() {
    return this.project.repository.name
  }

  private submit = async (args: PromptInputSubmitArgs) => {
    const settings: AiAgentTaskSettings = {
      ...this.taskSettings.getSettings(),
      llmProfile: args.llmId ?? null
    }

    try {
      const resolvedAttachments = await successfulAttachments(args.attachments)

      const task = await withGlobalLoader(this.context, this.context.queries.apiClient.createTask(this.project.key, this.repositoryName, {
        prompt: args.firstInputValue,
        contextItems: args.context,
        attachments: resolvedAttachments.map(item => item.id),
        elements: args.elements,
        initialBranchInfo: this.inputModel.branchSelection.get()?.selectedBaseBranch.get(),
        settings,
      }))

      void this.context.queryClient.invalidateQueries(tasksByRepoKey(this.project.key))

      await this.onSubmit()
      await this.onTaskCreated(task)
    } catch (err) {
      log.error("Failed to create task:", err)

      toast.error("Failed to create task")
    }
  }
}